import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useLocalStorage } from './useLocalStorage';
import type { UserProgress, UserSettings, QuestionProgress, Difficulty } from '@/types';

const defaultSettings: UserSettings = {
  soundEnabled: true,
  darkMode: false,
  selectedDifficulty: 'pup',
};

const defaultProgress: UserProgress = {
  questionsAnswered: {},
  speciesViewed: [],
  factsRead: [],
  streaks: {
    current: 0,
    longest: 0,
    lastActive: '',
  },
  milestones: {},
  lastCelebration: '',
  celebrationCount: 0,
  achievements: {
    unlocked: {},
    progress: {},
  },
  journeys: {},
  globalStats: {
    totalXp: 0,
    totalQuestionsAnswered: 0,
    totalCorrect: 0,
    speciesExplored: 0,
  },
  settings: defaultSettings,
};

interface ProgressContextValue {
  progress: UserProgress;
  // Question tracking
  recordAnswer: (questionId: string, correct: boolean, xpReward?: number) => void;
  getQuestionProgress: (questionId: string) => QuestionProgress | undefined;
  // Species tracking
  markSpeciesViewed: (speciesId: string) => void;
  hasViewedSpecies: (speciesId: string) => boolean;
  // Fact tracking
  markFactRead: (factId: string) => void;
  hasReadFact: (factId: string) => boolean;
  // Streak management
  updateStreak: () => void;
  // Settings
  updateSettings: (settings: Partial<UserSettings>) => void;
  // XP
  addXp: (amount: number) => void;
  // Stats helpers
  getCorrectAnswersForSpecies: (speciesId: string, questions: { id: string; relatedSpecies?: string[] }[]) => number;
  getCorrectAnswersForCategory: (category: string, questions: { id: string; category: string }[]) => number;
  getCorrectAnswersForDifficulty: (difficulty: Difficulty, questions: { id: string; difficulty: Difficulty }[]) => number;
  // Reset
  resetProgress: () => void;
}

const ProgressContext = createContext<ProgressContextValue | null>(null);

export function ProgressProvider({ children }: { children: React.ReactNode }) {
  const [progress, setProgress] = useLocalStorage<UserProgress>('otter-progress', defaultProgress);

  const recordAnswer = useCallback((questionId: string, correct: boolean, xpReward: number = 10) => {
    setProgress((prev) => {
      const now = new Date().toISOString();
      const existing = prev.questionsAnswered[questionId];
      const wasWrong = existing && !existing.lastCorrect;
      const isImprovement = wasWrong && correct;

      const newQuestionProgress: QuestionProgress = {
        attempts: (existing?.attempts || 0) + 1,
        lastCorrect: correct,
        firstCorrectDate: correct && !existing?.firstCorrectDate ? now : existing?.firstCorrectDate,
        history: [...(existing?.history || []), { date: now, correct }],
      };

      const xpGain = correct ? (isImprovement ? xpReward * 1.5 : xpReward) : 0;

      return {
        ...prev,
        questionsAnswered: {
          ...prev.questionsAnswered,
          [questionId]: newQuestionProgress,
        },
        globalStats: {
          ...prev.globalStats,
          totalXp: prev.globalStats.totalXp + xpGain,
          totalQuestionsAnswered: prev.globalStats.totalQuestionsAnswered + 1,
          totalCorrect: prev.globalStats.totalCorrect + (correct ? 1 : 0),
        },
      };
    });
  }, [setProgress]);

  const getQuestionProgress = useCallback((questionId: string): QuestionProgress | undefined => {
    return progress.questionsAnswered[questionId];
  }, [progress.questionsAnswered]);

  const markSpeciesViewed = useCallback((speciesId: string) => {
    setProgress((prev) => {
      if (prev.speciesViewed.includes(speciesId)) {
        return prev;
      }

      const xpGain = 5; // XP for first time viewing a species

      return {
        ...prev,
        speciesViewed: [...prev.speciesViewed, speciesId],
        globalStats: {
          ...prev.globalStats,
          totalXp: prev.globalStats.totalXp + xpGain,
          speciesExplored: prev.globalStats.speciesExplored + 1,
        },
      };
    });
  }, [setProgress]);

  const hasViewedSpecies = useCallback((speciesId: string): boolean => {
    return progress.speciesViewed.includes(speciesId);
  }, [progress.speciesViewed]);

  const markFactRead = useCallback((factId: string) => {
    setProgress((prev) => {
      if (prev.factsRead.includes(factId)) {
        return prev;
      }

      return {
        ...prev,
        factsRead: [...prev.factsRead, factId],
        globalStats: {
          ...prev.globalStats,
          totalXp: prev.globalStats.totalXp + 2, // Small XP for reading facts
        },
      };
    });
  }, [setProgress]);

  const hasReadFact = useCallback((factId: string): boolean => {
    return progress.factsRead.includes(factId);
  }, [progress.factsRead]);

  const updateStreak = useCallback(() => {
    setProgress((prev) => {
      const today = new Date().toISOString().split('T')[0];
      const lastActive = prev.streaks.lastActive;

      if (lastActive === today) {
        // Already active today
        return prev;
      }

      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      let newStreak = prev.streaks.current;

      if (lastActive === yesterday) {
        // Continuing streak
        newStreak = prev.streaks.current + 1;
      } else if (lastActive !== today) {
        // Streak broken, start fresh
        newStreak = 1;
      }

      const xpGain = newStreak > 1 ? Math.min(newStreak * 5, 50) : 0; // Streak bonus XP

      return {
        ...prev,
        streaks: {
          current: newStreak,
          longest: Math.max(newStreak, prev.streaks.longest),
          lastActive: today,
        },
        globalStats: {
          ...prev.globalStats,
          totalXp: prev.globalStats.totalXp + xpGain,
        },
      };
    });
  }, [setProgress]);

  const updateSettings = useCallback((settings: Partial<UserSettings>) => {
    setProgress((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        ...settings,
      },
    }));
  }, [setProgress]);

  const addXp = useCallback((amount: number) => {
    setProgress((prev) => ({
      ...prev,
      globalStats: {
        ...prev.globalStats,
        totalXp: prev.globalStats.totalXp + amount,
      },
    }));
  }, [setProgress]);

  const getCorrectAnswersForSpecies = useCallback((
    speciesId: string,
    questions: { id: string; relatedSpecies?: string[] }[]
  ): number => {
    const speciesQuestions = questions.filter(q => q.relatedSpecies?.includes(speciesId));
    return speciesQuestions.filter(q => progress.questionsAnswered[q.id]?.lastCorrect).length;
  }, [progress.questionsAnswered]);

  const getCorrectAnswersForCategory = useCallback((
    category: string,
    questions: { id: string; category: string }[]
  ): number => {
    const categoryQuestions = questions.filter(q => q.category === category);
    return categoryQuestions.filter(q => progress.questionsAnswered[q.id]?.lastCorrect).length;
  }, [progress.questionsAnswered]);

  const getCorrectAnswersForDifficulty = useCallback((
    difficulty: Difficulty,
    questions: { id: string; difficulty: Difficulty }[]
  ): number => {
    const difficultyQuestions = questions.filter(q => q.difficulty === difficulty);
    return difficultyQuestions.filter(q => progress.questionsAnswered[q.id]?.lastCorrect).length;
  }, [progress.questionsAnswered]);

  const resetProgress = useCallback(() => {
    setProgress(defaultProgress);
  }, [setProgress]);

  const value = useMemo(() => ({
    progress,
    recordAnswer,
    getQuestionProgress,
    markSpeciesViewed,
    hasViewedSpecies,
    markFactRead,
    hasReadFact,
    updateStreak,
    updateSettings,
    addXp,
    getCorrectAnswersForSpecies,
    getCorrectAnswersForCategory,
    getCorrectAnswersForDifficulty,
    resetProgress,
  }), [
    progress,
    recordAnswer,
    getQuestionProgress,
    markSpeciesViewed,
    hasViewedSpecies,
    markFactRead,
    hasReadFact,
    updateStreak,
    updateSettings,
    addXp,
    getCorrectAnswersForSpecies,
    getCorrectAnswersForCategory,
    getCorrectAnswersForDifficulty,
    resetProgress,
  ]);

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
}
